import { type ColumnDef } from "@tanstack/react-table";
import type { ChangeRequestDto, UserChangeRequestsDto } from "../../../../api";
import { ChangeRequestDtoStatusEnum } from "../../../../api";
import { Text } from "../../../../components/common/typography";
import { TableTextCell } from "../../../../components/common/table/text-cell";
import { Button } from "../../../../components/common/button";
import { ChangeRequestStatusBadge } from "../../../change-requests/presentation/components/change-request-status-badge";
import { humanizeType, humanizeTypeForAdmin } from "../../../change-requests/data/change-request-type";
import { formatDate } from "../../../../utils/date";
import type { SimpleTableColumn } from "../../../../components/common/simple-table";

export const adminChangeRequestsColumns: ColumnDef<ChangeRequestDto>[] = [
  {
    accessorKey: "userName",
    header: "Submitter",
    enableSorting: false,
    cell: ({ row }) => {
      const r = row.original;
      return (
        <div className="flex flex-col text-left">
          <Text variant="body" tone="dark">
            {r.userName || r.userId.slice(0, 8)}
          </Text>
          {r.userEmail && (
            <Text variant="bodyXs" tone="body" className="mt-1">
              {r.userEmail}
            </Text>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "type",
    header: "Type",
    enableSorting: true,
    cell: ({ row }) => <TableTextCell value={humanizeType(row.original.type)} />,
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: true,
    cell: ({ row }) => <ChangeRequestStatusBadge status={row.original.status} />,
  },
  {
    accessorKey: "submittedAt",
    header: "Submitted",
    enableSorting: true,
    cell: ({ row }) => (
      <TableTextCell
        value={
          row.original.submittedAt
            ? formatDate(row.original.submittedAt, true)
            : "—"
        }
      />
    ),
  },
];

function countBy(
  list: ChangeRequestDto[],
  status: ChangeRequestDtoStatusEnum,
): number {
  return list.filter((r) => r.status === status).length;
}

export const adminChangeRequestsByUserColumns: ColumnDef<UserChangeRequestsDto>[] = [
  {
    accessorKey: "userName",
    header: "User",
    enableSorting: false,
    cell: ({ row }) => {
      const user = row.original;
      return (
        <div className="flex flex-col text-left">
          <Text variant="body" tone="dark">
            {user.userName || "—"}
          </Text>
          {user.userEmail && (
            <Text variant="bodyXs" tone="body" className="mt-1">
              {user.userEmail}
            </Text>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "total",
    header: "Total",
    enableSorting: false,
    cell: ({ row }) => (
      <TableTextCell value={row.original.changeRequests?.length ?? 0} />
    ),
  },
  {
    accessorKey: "pending",
    header: "Pending",
    enableSorting: false,
    cell: ({ row }) => (
      <TableTextCell
        value={countBy(
          row.original.changeRequests ?? [],
          ChangeRequestDtoStatusEnum.Pending,
        )}
      />
    ),
  },
  {
    accessorKey: "changesRequested",
    header: "Changes requested",
    enableSorting: false,
    cell: ({ row }) => (
      <TableTextCell
        value={countBy(
          row.original.changeRequests ?? [],
          ChangeRequestDtoStatusEnum.ChangesRequested,
        )}
      />
    ),
  },
  {
    accessorKey: "approved",
    header: "Approved",
    enableSorting: false,
    cell: ({ row }) => (
      <TableTextCell
        value={countBy(
          row.original.changeRequests ?? [],
          ChangeRequestDtoStatusEnum.Approved,
        )}
      />
    ),
  },
  {
    accessorKey: "rejected",
    header: "Rejected",
    enableSorting: false,
    cell: ({ row }) => (
      <TableTextCell
        value={countBy(
          row.original.changeRequests ?? [],
          ChangeRequestDtoStatusEnum.Rejected,
        )}
      />
    ),
  },
];

// ── SimpleTable columns (used by the redesigned admin-change-requests-page) ──

export function buildChangeRequestsByUserColumns(
  onView: (userId: string) => void,
): SimpleTableColumn<UserChangeRequestsDto>[] {
  return [
    {
      key: "user",
      label: "User",
      className: "flex-[2] min-w-0",
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <Text variant="bodyBold" tone="dark">{row.userName || "—"}</Text>
          {row.userEmail && <Text variant="bodySm" tone="muted">{row.userEmail}</Text>}
        </div>
      ),
    },
    {
      key: "total",
      label: "Total",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{row.changeRequests?.length ?? 0}</Text>,
    },
    {
      key: "pending",
      label: "Pending",
      className: "flex-1",
      render: (row) => (
        <Text variant="body" tone="body">
          {countBy(row.changeRequests ?? [], ChangeRequestDtoStatusEnum.Pending)}
        </Text>
      ),
    },
    {
      key: "approved",
      label: "Approved",
      className: "flex-1",
      render: (row) => (
        <Text variant="body" tone="body">
          {countBy(row.changeRequests ?? [], ChangeRequestDtoStatusEnum.Approved)}
        </Text>
      ),
    },
    {
      key: "rejected",
      label: "Rejected",
      className: "flex-1",
      render: (row) => (
        <Text variant="body" tone="body">
          {countBy(row.changeRequests ?? [], ChangeRequestDtoStatusEnum.Rejected)}
        </Text>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      className: "flex-1 flex justify-end",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="!px-0 !text-textDark"
          onClick={(e) => { e.stopPropagation(); onView(row.userId); }}
        >
          View
        </Button>
      ),
    },
  ];
}

export function buildChangeRequestsAllColumns(
  onView: (id: string) => void,
): SimpleTableColumn<ChangeRequestDto>[] {
  return [
    {
      key: "submitter",
      label: "Submitter",
      className: "flex-[2] min-w-0",
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <Text variant="bodyBold" tone="dark">{row.userName || row.userId.slice(0, 8)}</Text>
          {row.userEmail && <Text variant="bodySm" tone="muted">{row.userEmail}</Text>}
        </div>
      ),
    },
    {
      key: "type",
      label: "Type",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{humanizeTypeForAdmin(row.type)}</Text>,
    },
    {
      key: "status",
      label: "Status",
      className: "flex-1",
      render: (row) => <ChangeRequestStatusBadge status={row.status} />,
    },
    {
      key: "submitted",
      label: "Submitted",
      className: "flex-1",
      render: (row) => (
        <Text variant="body" tone="body">
          {row.submittedAt ? formatDate(row.submittedAt, true) : "—"}
        </Text>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      className: "flex-1 flex justify-end",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="!px-0 !text-textDark"
          onClick={(e) => { e.stopPropagation(); onView(String(row.id)); }}
        >
          View
        </Button>
      ),
    },
  ];
}
